import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Upload, Users, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Subscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isCSVDialogOpen, setIsCSVDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  
  const [newSubscriber, setNewSubscriber] = useState({
    email: '',
    name: '',
    tags: '',
    domain_id: ''
  });
  
  const [bulkText, setBulkText] = useState('');
  const [csvFile, setCSVFile] = useState(null);

  useEffect(() => {
    loadDomains();
  }, []);

  useEffect(() => {
    if (domains.length > 0) {
      setSelectedDomain(domains[0].id);
      loadSubscribers(domains[0].id);
    }
  }, [domains]);

  const loadDomains = async () => {
    try {
      const response = await axios.get(`${API}/domains`, { headers: getAuthHeaders() });
      setDomains(response.data);
    } catch (error) {
      toast.error('Failed to load domains');
    }
  };

  const loadSubscribers = async (domainId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subscribers?domain_id=${domainId}`, { headers: getAuthHeaders() });
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newSubscriber,
        tags: newSubscriber.tags.split(',').map(t => t.trim()).filter(t => t),
        domain_id: selectedDomain
      };
      
      await axios.post(`${API}/subscribers`, data, { headers: getAuthHeaders() });
      toast.success('Subscriber added successfully');
      setNewSubscriber({ email: '', name: '', tags: '', domain_id: '' });
      setIsAddDialogOpen(false);
      loadSubscribers(selectedDomain);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subscriber');
    }
  };

  const handleBulkAdd = async () => {
    try {
      const lines = bulkText.split('\n').filter(line => line.trim());
      const subscribers = lines.map(line => {
        const [email, name] = line.split(',').map(s => s.trim());
        return { email, name: name || '' };
      });
      
      await axios.post(`${API}/subscribers/bulk`, {
        subscribers,
        domain_id: selectedDomain
      }, { headers: getAuthHeaders() });
      
      toast.success('Subscribers added successfully');
      setBulkText('');
      setIsBulkDialogOpen(false);
      loadSubscribers(selectedDomain);
    } catch (error) {
      toast.error('Failed to add subscribers');
    }
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('domain_id', selectedDomain);
      
      const response = await axios.post(`${API}/subscribers/import-csv`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Added ${response.data.added} subscribers (${response.data.skipped} skipped)`);
      setCSVFile(null);
      setIsCSVDialogOpen(false);
      loadSubscribers(selectedDomain);
    } catch (error) {
      toast.error('Failed to import CSV');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    
    try {
      await axios.delete(`${API}/subscribers/${id}`, { headers: getAuthHeaders() });
      toast.success('Subscriber deleted');
      loadSubscribers(selectedDomain);
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
              Subscribers
            </h1>
            <p className="text-lg text-[#9CA3AF]">
              Manage your email subscribers
            </p>
          </div>
          
          <div className="flex gap-2">
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-subscriber-button"
                  className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
                >
                  <Plus size={20} className="mr-2" />
                  Add Subscriber
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#13151A] border-[#2A2D36] text-[#F3F4F6]">
                <DialogHeader>
                  <DialogTitle className="text-[#F3F4F6]">Add New Subscriber</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSubscriber} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newSubscriber.email}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, email: e.target.value })}
                      className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={newSubscriber.name}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })}
                      className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={newSubscriber.tags}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, tags: e.target.value })}
                      placeholder="newsletter, premium"
                      className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white">
                    Add Subscriber
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#2A2D36] text-[#F3F4F6]">
                  <Users size={20} className="mr-2" />
                  Bulk Add
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#13151A] border-[#2A2D36] text-[#F3F4F6]">
                <DialogHeader>
                  <DialogTitle className="text-[#F3F4F6]">Bulk Add Subscribers</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Label>Paste emails (one per line, format: email,name)</Label>
                  <Textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder="user@example.com,John Doe&#10;user2@example.com,Jane Smith"
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] min-h-[200px] font-mono text-sm"
                  />
                  <Button onClick={handleBulkAdd} className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white">
                    Add Subscribers
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isCSVDialogOpen} onOpenChange={setIsCSVDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#2A2D36] text-[#F3F4F6]">
                  <Upload size={20} className="mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#13151A] border-[#2A2D36] text-[#F3F4F6]">
                <DialogHeader>
                  <DialogTitle className="text-[#F3F4F6]">Import Subscribers from CSV</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="p-4 rounded-md bg-[#183871]/10 border border-[#183871]/20">
                    <p className="text-sm text-[#9CA3AF]">
                      CSV Format: email, name, tags<br />
                      Example: user@example.com, John Doe, newsletter
                    </p>
                  </div>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCSVFile(e.target.files[0])}
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                  />
                  <Button onClick={handleCSVImport} className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white">
                    Import CSV
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Domain Selector */}
        {domains.length > 0 && (
          <div className="mb-6">
            <Label className="text-[#F3F4F6] mb-2">Filter by Domain</Label>
            <select
              value={selectedDomain}
              onChange={(e) => {
                setSelectedDomain(e.target.value);
                loadSubscribers(e.target.value);
              }}
              className="w-64 px-4 py-2 rounded-md bg-[#1F2128] border border-[#2A2D36] text-[#F3F4F6]"
            >
              {domains.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domain.domain}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Subscribers Table */}
        <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm overflow-hidden shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]">
          {loading ? (
            <p className="text-[#9CA3AF] text-center py-12">Loading subscribers...</p>
          ) : subscribers.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-[#9CA3AF] mb-4" />
              <p className="text-[#9CA3AF] mb-4">No subscribers yet</p>
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
              >
                Add Your First Subscriber
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1F2128] border-b border-[#2A2D36]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2A2D36]">
                  {subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-[#1F2128] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#F3F4F6]">
                        {subscriber.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#9CA3AF]">
                        {subscriber.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-1 flex-wrap">
                          {subscriber.tags && subscriber.tags.length > 0 ? (
                            subscriber.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 rounded-md bg-[#183871]/20 text-[#183871] text-xs"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-[#9CA3AF]">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {subscriber.subscribed ? (
                          <span className="px-2 py-1 rounded-md bg-[#10B981]/20 text-[#10B981] text-xs font-medium">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-md bg-[#EF4444]/20 text-[#EF4444] text-xs font-medium">
                            Unsubscribed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Button
                          onClick={() => handleDelete(subscriber.id)}
                          size="sm"
                          variant="outline"
                          className="border-[#2A2D36] text-[#EF4444] hover:bg-[#EF4444]/10"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-[#9CA3AF]">
          Total: {subscribers.length} subscribers
          {subscribers.filter(s => s.subscribed).length !== subscribers.length && (
            <span className="ml-4">
              ({subscribers.filter(s => s.subscribed).length} active)
            </span>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Subscribers;
